import { useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import useStore from '../store'
import { useLifeCanvas } from '../hooks/useLifeCanvas'
import {
  weeksLived, weeksLeft, totalWeeks,
  fmtYearsMo, fmtWeeks, allocatedWeeks,
  currentAge, lifePercent
} from '../lib/timeline'

const ZOOMS = ['week', 'month', 'year', 'decade']
const ZOOM_DESC = {
  week:   'Each block = 1 week',
  month:  'Each block = 1 month (~4 weeks)',
  year:   'Each block = 1 year',
  decade: 'Each block = 1 decade',
}

export default function CanvasPage() {
  const ref         = useRef(null)
  const profile     = useStore(s => s.profile)
  const goals       = useStore(s => s.goals)
  const zoom        = useStore(s => s.zoom)
  const setZoom     = useStore(s => s.setZoom)
  const showDrift   = useStore(s => s.showDrift)
  const toggleDrift = useStore(s => s.toggleDrift)
  const has         = useStore(s => s.has)
  const constraints = useStore(s => s.constraints)

  const [showGuide, setShowGuide] = useState(false)

  useLifeCanvas(ref, { profile, goals, zoom, showDrift })

  if (!profile) return null

  const lived     = weeksLived(profile.date_of_birth)
  const left      = weeksLeft(profile.date_of_birth, profile.expected_lifespan)
  const total     = totalWeeks(profile.expected_lifespan)
  const allocated = allocatedWeeks(goals)
  const freeLeft  = left - allocated
  const age       = currentAge(profile.date_of_birth)
  const pct       = lifePercent(profile.date_of_birth, profile.expected_lifespan).toFixed(1)

  const hasActiveGoals = goals.some(g => ['active', 'committed', 'paused'].includes(g.status))
  const hasAnyGoals    = goals.length > 0

  return (
    <motion.div
      className="flex flex-col h-full"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
    >
      {/* ── Stats bar ── */}
      <div className="shrink-0 border-b border-m-border">
        <div className="px-5 py-3 grid grid-cols-5 gap-4">
          <StatBlock label="Your age"       value={`${age}`}              sub="years" />
          <StatBlock label="Time lived"     value={fmtYearsMo(lived)}     accent />
          <StatBlock label="Time remaining" value={fmtYearsMo(left)}      highlight />
          <StatBlock label="Allocated"      value={fmtWeeks(allocated)}   dim={!allocated}
            tooltip={allocated ? `${allocated} weeks assigned to active goals` : 'No goals allocated yet — go to Goals to add one'} />
          <StatBlock label="Life elapsed"   value={`${pct}%`}             dim />
        </div>
      </div>

      {/* ── Constraint warnings ── */}
      <AnimatePresence>
        {constraints && (constraints.status === 'block' || constraints.status === 'warn' || constraints.cogFlagged || constraints.lifeFlagged) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="shrink-0 border-b border-m-border px-5 py-2 flex flex-col gap-1.5 overflow-hidden"
          >
            {constraints.status === 'block' && (
              <div className="alert-danger">
                ⚠ Time overload — {constraints.overage.toFixed(1)} hrs/wk over capacity. Pause or remove an active goal.
              </div>
            )}
            {constraints.status === 'warn' && (
              <div className="alert-warn">
                Approaching capacity: {constraints.load.toFixed(1)} of {profile.weekly_available_hours} hrs/wk used.
              </div>
            )}
            {constraints.cogFlagged && (
              <div className="alert-warn">High cognitive load — too many demanding goals running simultaneously.</div>
            )}
            {constraints.lifeFlagged && (
              <div className="alert-danger">Allocated time exceeds your remaining lifetime. Reduce goal scope.</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Controls bar ── */}
      <div className="shrink-0 border-b border-m-border px-5 py-2 flex items-center justify-between gap-4">
        {/* Zoom */}
        <div className="flex items-center gap-1">
          {ZOOMS.map(z => (
            <button key={z} onClick={() => setZoom(z)}
              title={ZOOM_DESC[z]}
              className={`font-mono text-[10px] tracking-widest uppercase px-3 py-1 border transition-all duration-150 ${
                zoom === z
                  ? 'border-m-accent text-m-accent bg-m-accent/5'
                  : 'border-m-border text-m-faint hover:text-m-subtle hover:border-m-dim'
              }`}>
              {z}
            </button>
          ))}
          <span className="font-mono text-[9px] text-m-faint ml-2 hidden sm:block">
            {ZOOM_DESC[zoom]}
          </span>
        </div>

        <div className="flex items-center gap-4">
          {/* Drift toggle (unlocked) */}
          {has('drift_layer') && (
            <button onClick={toggleDrift}
              className={`font-mono text-[10px] flex items-center gap-1.5 transition-colors ${showDrift ? 'text-m-warn' : 'text-m-faint hover:text-m-subtle'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${showDrift ? 'bg-m-warn' : 'bg-m-faint'}`} />
              Unallocated time
            </button>
          )}

          {/* Guide toggle */}
          <button onClick={() => setShowGuide(g => !g)}
            className={`font-mono text-[10px] transition-colors ${showGuide ? 'text-m-accent' : 'text-m-faint hover:text-m-subtle'}`}>
            {showGuide ? 'Hide guide' : 'How to read this'}
          </button>
        </div>
      </div>

      {/* ── Guide panel ── */}
      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="shrink-0 border-b border-m-border overflow-hidden"
          >
            <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <GuideItem color="#131313" border label="Past weeks" desc="Already lived. Cannot be recovered." />
              <GuideItem color="#0c0c0c" border label="Available weeks" desc="Future time not yet allocated to any goal." />
              <GuideItem color="#a8ff3e" label="Goal allocation" desc="Weeks reserved for your active committed goals." />
              <GuideItem color="#1a1800" label="Unallocated past" desc="Past weeks with no active goal (visible when enabled)." dim />
            </div>
            <div className="px-5 pb-3">
              <p className="font-mono text-[10px] text-m-faint leading-relaxed">
                The canvas updates once per day. Zoom levels change how many weeks each block represents.
                Add goals in the Goals tab — committed goals will appear here as coloured allocations.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Canvas + Empty State ── */}
      <div className="flex-1 overflow-auto relative">
        {/* Canvas always renders */}
        <div className="p-5">
          <canvas ref={ref} style={{ imageRendering: 'pixelated', display: 'block', maxWidth: '100%' }} />
        </div>

        {/* Empty state overlay — only when no committed goals */}
        {!hasActiveGoals && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: 'linear-gradient(to bottom, transparent 30%, #080808 70%)' }}
          >
            <div className="text-center max-w-sm px-6 pb-8">
              <p className="font-mono text-xl text-m-text mb-2">
                {hasAnyGoals ? 'No goals on the canvas yet.' : 'Your canvas is empty.'}
              </p>
              <p className="font-mono text-xs text-m-subtle leading-relaxed mb-5">
                {hasAnyGoals
                  ? 'You have goals in backlog. Commit one to see it allocated on your timeline.'
                  : 'Add a committed goal and it will appear here as a coloured allocation on your life timeline.'
                }
              </p>
              <Link to="/goals" className="btn-accent-ghost btn inline-block">
                {hasAnyGoals ? 'Commit a goal →' : 'Add your first goal →'}
              </Link>
            </div>
          </motion.div>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="shrink-0 border-t border-m-border px-5 py-2.5 flex items-center gap-5 flex-wrap">
        <LegendItem color="#131313" label="Past" />
        <LegendItem color="#0c0c0c" border label="Available" />
        {goals
          .filter(g => ['committed','active','paused'].includes(g.status))
          .slice(0, 6)
          .map(g => (
            <LegendItem key={g.id} color={g.color} label={g.title.length > 20 ? g.title.slice(0, 20) + '…' : g.title} />
          ))
        }
        {showDrift && has('drift_layer') && <LegendItem color="#1a1800" label="Unallocated past" />}
        {!hasActiveGoals && (
          <span className="font-mono text-[9px] text-m-faint ml-auto">
            Colours appear when you commit goals
          </span>
        )}
      </div>
    </motion.div>
  )
}

function StatBlock({ label, value, sub, accent, highlight, dim, tooltip }) {
  return (
    <div className="group relative">
      <p className="label mb-1">{label}</p>
      <p className={`font-mono text-xl leading-none ${
        accent    ? 'text-m-accent' :
        highlight ? 'text-m-text'   :
        dim       ? 'text-m-faint'  : 'text-m-subtle'
      }`}>
        {value}
        {sub && <span className="text-m-subtle text-xs ml-1">{sub}</span>}
      </p>
      {tooltip && (
        <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-10">
          <div className="bg-m-raised border border-m-border px-2 py-1 font-mono text-[10px] text-m-subtle whitespace-nowrap">
            {tooltip}
          </div>
        </div>
      )}
    </div>
  )
}

function GuideItem({ color, label, desc, border, dim }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-4 h-4 shrink-0 mt-0.5"
        style={{
          backgroundColor: color,
          outline: border ? '1px solid #1c1c1c' : 'none',
          opacity: dim ? 0.5 : 1,
        }} />
      <div>
        <p className="font-mono text-[11px] text-m-text">{label}</p>
        <p className="font-mono text-[9px] text-m-faint leading-relaxed">{desc}</p>
      </div>
    </div>
  )
}

function LegendItem({ color, label, border }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: color, outline: border ? '1px solid #1c1c1c' : undefined }} />
      <span className="font-mono text-[10px] text-m-faint">{label}</span>
    </div>
  )
}
